import NoteComposer from './NoteComposer';

// One note, and a list of them. Shared by the client's Notes tab and the Notes
// screen so the two can never drift into showing a note differently.
//
// ⚠️ NOTHING HERE DECIDES WHAT IS VISIBLE OR EDITABLE. The server returns only
// notes this caller may read, and stamps each one with `canEdit`. This file
// reads those answers; it must never grow a rule that looks like it computes
// them, because the next person to read it will believe it.

const fmtWhen = (d) => (d
  ? new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
  })
  : '');

const initials = (name) => (name || '?')
  .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0])
  .join('')
  .toUpperCase();

export default function NoteList({
  notes,
  // Editing state lives with the caller, which owns the reload after a save.
  editingId,
  editDraft,
  setEditDraft,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onTogglePin,
  onDelete,
  saving = false,
  orgUsers = [],
  // Same contract as NoteComposer: a list turns the picker on, so a note can be
  // re-linked (or unlinked) from wherever you happen to be reading it.
  clients = null,
  // Show which client a note belongs to. Off on the client's own tab, where
  // every row has the same answer and printing it would be noise.
  showClient = false,
  onOpenClient,
}) {
  return (
    <ul className="cn-list">
      {notes.map((n) => (
        <li key={n.id} className={`cn-item ${n.isPinned ? 'is-pinned' : ''}`}>
          <div className="cn-item-head">
            <span className="cn-avatar" aria-hidden>{initials(n.author.name)}</span>
            <div className="cn-meta">
              <span className="cn-author">{n.author.name}{n.isMine && <em> (you)</em>}</span>
              <span className="cn-when">
                {fmtWhen(n.createdAt)}
                {n.editedAt && <em> · edited {fmtWhen(n.editedAt)} by {n.editedByName}</em>}
              </span>
            </div>
            <span style={{ flex: 1 }} />
            {/* A general note says so rather than showing a blank where a
                client would be — "no client" is a property of the note, not a
                missing value somebody should go and fill in. */}
            {showClient && (n.client
              ? (
                <button
                  type="button"
                  className="cn-pill cn-pill-client"
                  onClick={() => onOpenClient?.(n.client)}
                  title={`Open ${n.client.name}`}
                >
                  {n.client.name}{n.client.code ? ` · ${n.client.code}` : ''}
                </button>
              )
              : <span className="cn-pill cn-pill-general">General</span>
            )}
            {n.isPinned && <span className="cn-pill cn-pill-pin">📌 Pinned</span>}
            {n.visibility === 'personal' && (
              <span className="cn-pill cn-pill-lock" title="Only the author and anyone they tagged can see this">
                🔒 Personal
              </span>
            )}
            {n.taggedMe && <span className="cn-pill cn-pill-you">You were tagged</span>}
          </div>

          {editingId === n.id ? (
            <NoteComposer
              value={editDraft}
              onChange={setEditDraft}
              onSubmit={() => onSaveEdit(n.id)}
              onCancel={onCancelEdit}
              submitLabel="Save changes"
              saving={saving}
              orgUsers={orgUsers}
              clients={clients}
            />
          ) : (
            <>
              <p className="cn-body cn-body-full">{n.body}</p>
              {n.tagged.length > 0 && (
                <div className="cn-tagged">
                  {n.tagged.map((t) => <span className="cn-tagged-chip" key={t.id}>@{t.name}</span>)}
                </div>
              )}
              {/* canEdit comes from the SERVER — the author or an admin. The UI
                  never offers what the API would refuse. */}
              {n.canEdit && (
                <div className="cn-item-actions">
                  <button type="button" className="cn-link" onClick={() => onStartEdit(n)}>Edit</button>
                  <button type="button" className="cn-link" onClick={() => onTogglePin(n)}>
                    {n.isPinned ? 'Unpin' : 'Pin'}
                  </button>
                  <button type="button" className="cn-link cn-link-danger" onClick={() => onDelete(n)}>Delete</button>
                </div>
              )}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
